import cronstrue from 'cronstrue'

/**
 *
 Validates AWS EventBridge cron schedule expressions.
 Copied from https://github.com/gharibyan/aws-cron-expression-validator/blob/main/src/aws-cron-expression-validator.js
 https://docs.aws.amazon.com/eventbridge/latest/userguide/eb-create-rule-schedule.html#eb-cron-expressions

 | Field        | Values          | Wildcards     |
 | :----------: | :-------------: | :-----------: |
 | Minute       | 0-59            | , - * /       |
 | Hour         | 0-23            | , - * /       |
 | Day-of-month | 1-31            | , - * ? / L W |
 | Month        | 1-12 or JAN-DEC | , - * /       |
 | Day-of-week  |  1-7 or SUN-SAT | , - * ? L #   |
 | Year         | 1970-2199       | , - * /       |
 *
 **/
const minuteValues = '(0?[0-9]|[1-5][0-9])' // [0]0-59
const hourValues = '(0?[0-9]|1[0-9]|2[0-3])' // [0]0-23
const monthOfDayValues = '(0?[1-9]|[1-2][0-9]|3[0-1])' // [0]1-31
const monthValues = '(0?[1-9]|1[0-2]|JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)' // [0]1-12 or JAN-DEC
const dayOfWeekValues = '([1-7]|SUN|MON|TUE|WED|THU|FRI|SAT)' // 1-7 or SUN-SAT
const dayOfWeekHash = `(${dayOfWeekValues}#[1-5])` // Day of the week in the Nth week of the month
const yearValues = '((19[7-9][0-9])|(2[0-1][0-9][0-9]))' // 1970-2199

const rangeRegex = (values: string) =>
  `(${values}|(\\*-${values})|(${values}-${values})|(${values}-\\*))`

const listRegex = (values: string) => {
  const range = rangeRegex(values)
  return `(${range}(,${range})*)`
}

const slashRegex = (values: string) => `((\\*|${values})\\/[0-9]*[1-9][0-9]*)`

const commonRegex = (values: string) => `(${listRegex(values)}|\\*|${slashRegex(values)})`

const minuteRegex = () => new RegExp(`^(${commonRegex(minuteValues)})$`)
const hourRegex = () => new RegExp(`^(${commonRegex(hourValues)})$`)
const dayOfMonthRegex = () =>
  new RegExp(`^(${commonRegex(monthOfDayValues)}|\\?|L|${monthOfDayValues}W)$`)
const monthRegex = () => new RegExp(`^(${commonRegex(monthValues)})$`)
const dayOfWeekRegex = () =>
  new RegExp(`^(${listRegex(dayOfWeekValues)}|\\*|\\?|L|${dayOfWeekHash})$`)
const yearRegex = () => new RegExp(`^(${commonRegex(yearValues)}|\\?|L)$`)

export interface CronValidationResult {
  minute: string
  hour: string
  dayOfMonth: string
  month: string
  dayOfWeek: string
  year: string
  expression: string
}

export const awsCronExpressionValidator = (expression: string): CronValidationResult => {
  const valueCount = expression.split(' ').length
  if (valueCount !== 6) {
    throw new Error(
      `Incorrect number of values in '${expression}'. 6 required, ${valueCount} provided.`
    )
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek, year] = expression.split(' ')

  if (!((dayOfMonth === '?' && dayOfWeek !== '?') || (dayOfMonth !== '?' && dayOfWeek === '?'))) {
    throw new Error(
      `Invalid combination of day-of-month '${dayOfMonth}' and day-of-week '${dayOfWeek}'. One must be a question mark (?)`
    )
  }
  if (!minuteRegex().test(minute)) {
    throw new Error(`Invalid minute value '${minute}'.`)
  }
  if (!hourRegex().test(hour)) {
    throw new Error(`Invalid hour value '${hour}'.`)
  }
  if (!dayOfMonthRegex().test(dayOfMonth)) {
    throw new Error(`Invalid day-of-month value '${dayOfMonth}'.`)
  }
  if (!monthRegex().test(month)) {
    throw new Error(`Invalid month value '${month}'.`)
  }
  if (!dayOfWeekRegex().test(dayOfWeek)) {
    throw new Error(`Invalid day-of-week value '${dayOfWeek}'.`)
  }
  if (!yearRegex().test(year)) {
    throw new Error(`Invalid year value '${year}'.`)
  }

  return {
    minute,
    hour,
    dayOfMonth,
    month,
    dayOfWeek,
    year,
    expression
  }
}

export const extractCronValue = (cronExpression: string): string => {
  return cronExpression.match(/cron\((.*)\)/)?.[1] || ''
}

export const formatCronExpression = (cronPart: string): string => {
  try {
    return cronstrue.toString(cronPart, {
      locale: 'en',
      dayOfWeekStartIndexZero: false,
      monthStartIndexZero: false
    })
  } catch (err) {
    return 'Invalid cron expression'
  }
}

export const humanizeCron = (cronExpression: string, timezone: string) => {
  try {
    let cronPart = cronExpression

    if (cronExpression.includes('cron(')) {
      cronPart = extractCronValue(cronExpression)
    }

    if (!cronPart) {
      return 'Invalid cron expression'
    }

    const humanReadable = formatCronExpression(cronPart)

    return `${humanReadable}`
  } catch (err) {
    return 'Invalid cron expression'
  }
}
